import { skipToken, useQuery } from "@tanstack/react-query";
import Button from "components/ui/Button";
import bookmarkFilledImg from "images/bookmark-filled.svg";
import bookmarkImg from "images/bookmark.svg";
import {
  DictionarySearchResponse,
  personalDictionaryQuery,
  useAddSavedWord,
  useDeleteSavedWord,
} from "services/dictionary";
import { useTranslation } from "react-i18next";
import DictionaryItem from "./DictionaryItem";
import "../tabs.css";

type DefineTabProps = {
  defineContent: DictionarySearchResponse | string | null;
};

export default function DefineTab({ defineContent }: DefineTabProps) {
  const { t } = useTranslation();

  // Query the saved list scoped to the displayed word, to tell if it's bookmarked.
  const lookupWord =
    typeof defineContent === "object" && defineContent !== null
      ? defineContent.word
      : undefined;
  const savedWordsOptions = personalDictionaryQuery({ filter: lookupWord });
  const savedWordsResult = useQuery({
    ...savedWordsOptions,
    queryFn: lookupWord ? savedWordsOptions.queryFn : skipToken,
  });
  const savedWords = savedWordsResult.data?.list ?? [];

  const addWord = useAddSavedWord();
  const deleteWord = useDeleteSavedWord();

  const onToggleWord = (content: DictionarySearchResponse) => {
    const item = savedWords.find(({ word }) => word === content.word);
    if (item) {
      deleteWord.mutate(item.id);
    } else {
      addWord.mutate({ word: content.word });
    }
  };

  if (defineContent === null) {
    return null;
  }

  if (typeof defineContent === "string") {
    return (
      <div className="dictionary-items">
        <span>{defineContent}</span>
      </div>
    );
  }

  const isSaved = savedWords.some(({ word }) => word === defineContent.word);

  return (
    <div className="dictionary-items">
      <div className="dictionary-word-wrapper">
        <span className="dictionary-word">{defineContent.word}</span>
        <Button
          variant="plain"
          className="dictionary-action"
          aria-label={t("Common.actions.saveWord")}
          aria-pressed={isSaved}
          onClick={() => onToggleWord(defineContent)}
        >
          <img src={isSaved ? bookmarkFilledImg : bookmarkImg} alt="" />
        </Button>
      </div>
      {Object.entries(defineContent.pos).map(([pos, entries], index) => (
        <DictionaryItem key={index} pos={pos} items={entries} />
      ))}
    </div>
  );
}
